import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import GMark from "@/components/GMark";
import CommunityMarquee from "@/components/CommunityMarquee";
import "@/components/CommunityMarquee.css";
import Seo from "@/components/Seo";
import PricingSection from "@/components/PricingSection";
import FooterMeta from "@/components/FooterMeta";
import { gateAndGatherOrganization, gateAndGatherWebSite } from "@/lib/schema";

const QUESTIONS = [
  "Is it available?",
  "How many have you got?",
  "How much is it?",
  "Where are you?",
  "Can I collect or have it delivered?",
  "How do I pay?",
];

// Six Questions — quietly ticks each row off in sequence the moment the block
// scrolls into view. No strikethrough on the questions themselves: a strike
// reads as "removed / no longer relevant", but the story here is the opposite —
// your Gate has this covered. Reduced-motion users see all six ticked instantly.
function SixQuestions() {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [answered, setAnswered] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setAnswered(QUESTIONS.length); return; }
    const el = ref.current;
    if (!el) return;
    const timers = [];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            io.disconnect();
            QUESTIONS.forEach((_, i) => {
              // 380ms head-start so the first tick lands just after the block settles,
              // then ~420ms cadence — reads as "tick, tick, tick" rather than a rush.
              const id = setTimeout(() => setAnswered((n) => Math.max(n, i + 1)), 380 + i * 420);
              timers.push(id);
            });
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      // Clear pending ticks so a mid-animation navigate doesn't setState on
      // an unmounted component.
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} data-testid="six-questions-block">
      <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80 mb-3">The six questions</p>
      <h2
        data-testid="six-questions-h2"
        className="font-serif-heading text-[#20251F] tracking-tight leading-[1.05]"
        style={{ fontSize: "clamp(1.5rem, 4.2vw, 3rem)" }}
      >
        The six questions you answer every{" "}
        <s className="text-stone-400 decoration-stone-500 decoration-2" data-testid="six-questions-strike">week</s>{" "}
        <span className="italic">day</span>.
      </h2>
      <p className="mt-2 text-base sm:text-lg text-stone-700 max-w-2xl leading-relaxed">
        Your Gate answers them before anyone has to ask.
      </p>
      <ul className="mt-5 sm:mt-6 max-w-lg space-y-1.5 sm:space-y-2" data-testid="six-questions-list">
        {QUESTIONS.map((q, i) => {
          const done = i < answered;
          return (
            <li
              key={q}
              data-testid={`six-q-${i}`}
              data-answered={done ? "true" : "false"}
              className="flex items-center gap-3 rounded-2xl border bg-white px-3.5 py-2 sm:py-2.5 transition-colors duration-300"
              style={{ borderColor: done ? "rgba(63,103,78,0.4)" : "rgba(222,213,195,0.9)" }}
            >
              <span
                aria-hidden="true"
                className="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 shrink-0"
                style={{
                  background: done ? "#3F674E" : "#F5F0E5",
                  border: done ? "1px solid #3F674E" : "1px dashed #C9BFA8",
                  transform: done ? "scale(1)" : "scale(0.92)",
                }}
              >
                {done && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </span>
              <span className="text-sm sm:text-[15px] text-[#20251F] leading-snug">{q}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F5F0E5] text-[#20251F]">
      <Seo
        title="Gate + Gather | Sell What You Grow Online NZ"
        description="Gate + Gather gives independent NZ growers their own simple storefront to show what's available, take orders and keep track of stock."
        path="/"
        jsonLd={[gateAndGatherOrganization(), gateAndGatherWebSite()]}
      />
      <header className="border-b border-[#DED5C3] bg-[#F5F0E5]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="nav-brand">
            <GMark size={28} className="rounded-lg shrink-0" />
            <span className="font-serif-heading text-lg sm:text-xl tracking-tight whitespace-nowrap">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <a href="#pricing" data-testid="nav-pricing-link" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline">Pricing</a>
            <Link to="/faq" data-testid="nav-faq-link" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline">FAQ</Link>
            <Link to="/seller/login" data-testid="nav-login-link" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-2 sm:px-3 py-2 whitespace-nowrap">Log in</Link>
            <Link to="/seller/signup" data-testid="nav-signup-link">
              <Button className="bg-[#20251F] hover:bg-black rounded-full text-white h-9 sm:h-10 px-3 sm:px-4 text-sm whitespace-nowrap" data-testid="nav-signup-btn">Open your Gate</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO — single centred-left column. New copy leads with the promise
          ("your own simple storefront…") and names the product ("We call it
          your Gate.") at the end. No brand-plus sigil in the heading. */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-10 sm:pb-14 text-center" data-testid="hero">
        <p className="font-mono-label text-xs sm:text-[13px] font-medium uppercase tracking-[0.16em] text-[#3F674E]" data-testid="hero-eyebrow">
          For independent growers
        </p>
        <h1 className="mt-5 mx-auto max-w-[900px] font-serif-heading font-normal text-[38px] sm:text-5xl lg:text-[62px] leading-[1.1] tracking-[-0.015em] text-[#20251F] [text-wrap:balance]" data-testid="hero-headline">
          Your own storefront for selling what you grow.
        </h1>
        <p className="mt-6 mx-auto max-w-[680px] text-[17px] sm:text-lg lg:text-[19px] leading-[1.5] text-[#20251F]/75 [text-wrap:pretty]" data-testid="hero-body">
          Keep selling where you already sell. Give your customers one simple place to see what's available and place an order.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-xs sm:max-w-none mx-auto" data-testid="hero-cta-row">
          <Link to="/seller/signup"><Button size="lg" className="w-full sm:w-auto bg-[#20251F] hover:bg-black rounded-full h-12 px-7 text-[#FFFCF6]" data-testid="hero-signup-btn">Create your Gate <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          <Link to="/g/quail-lane"><Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full h-12 px-7 bg-transparent border-[#DED5C3] text-[#20251F] hover:bg-[#FFFCF6]" data-testid="hero-demo-btn">See an example Gate</Button></Link>
        </div>
        <p className="mt-5 text-[13px] sm:text-sm text-[#6B6E67]" data-testid="hero-reassurance">
          Your Gate. Your customers. Not a marketplace.
        </p>
      </section>

      {/* Community marquee — sits directly under the hero so real growers land
          in front of the visitor before another text-heavy section does. The
          marquee + Six Questions below form one visual chapter: real people,
          then the repetitive questions their Gate quietly handles. */}
      <CommunityMarquee />

      {/* SIX QUESTIONS — flush under the marquee (no top border, tight top
          padding) so the two blocks read as one chapter. Ticks animate as the
          list scrolls into view; see SixQuestions component. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-8 pb-8 sm:pb-14 text-left">
        <SixQuestions />
      </section>

      {/* FOUR STEPS · pulled from the /for-growers walk-through. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 border-t border-stone-200 text-left" data-testid="four-step-section">
        <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80 mb-3">How it works</p>
        <h2
          data-testid="four-steps-h2"
          className="font-serif-heading text-[#20251F] tracking-tight leading-[1.02] whitespace-nowrap"
          style={{ fontSize: "clamp(1.375rem, 5.6vw, 4.75rem)" }}
        >
          Four steps. Then you're selling.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-7 sm:mt-10">
          {[
            {
              step: "1",
              label: "Open your Gate",
              time: "Under 3 minutes",
              copy: "Name your Gate, say where you're based, add your first product. Then we ask for your email.",
            },
            {
              step: "2",
              label: "Add what you're selling",
              time: "Under 60 seconds",
              copy: "Add a photo, price and how many you've got.",
            },
            {
              step: "3",
              label: "Update stock",
              time: "Under 5 seconds",
              copy: "Sold four at the market? Tap the − button four times. That's it.",
            },
            {
              step: "4",
              label: "Share your Gate",
              time: "gateandgather.co.nz/g/yourname",
              copy: "Put your link on Facebook or Instagram, send it to your regulars, or pop your QR code at the gate or market.",
            },
          ].map((s) => (
            <div
              key={s.step}
              className="p-5 sm:p-6 rounded-2xl border border-stone-200 bg-white flex flex-col"
              data-testid={`four-step-card-${s.step}`}
            >
              <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">
                <span className="text-[#94AE29] mr-1">+</span>{s.step} · {s.label}
              </p>
              <p className="font-serif-heading text-lg text-[#20251F] mt-2 leading-tight">{s.time}</p>
              <p className="text-sm text-stone-700 mt-2 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING — visible before signup, config-driven from /api/plans.
          The pricing card footer carries the single, canonical "your sales
          stay yours" reassurance — no separate commission box above. */}
      <PricingSection eyebrow="Pricing" heading="Choose your Gate." />

      {/* BRAND LINE + FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 border-t border-stone-200 text-left">
        <h2
          data-testid="built-for-h2"
          className="font-serif-heading text-[#20251F] tracking-tight leading-[1.02]"
          style={{ fontSize: "clamp(1.375rem, 5.6vw, 4.75rem)" }}
        >
          Built for growers, with a phone, a wheelbarrow and no time to waste.
        </h2>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mt-7 sm:mt-10">
          <div className="p-5 sm:p-6 rounded-2xl border border-stone-200 bg-white">
            <p className="font-mono-label text-[11px] uppercase tracking-widest text-[#3F674E]/70">Add a product</p>
            <div className="mt-3 rounded-xl border border-dashed border-stone-300 aspect-[4/3] bg-stone-50 flex items-center justify-center text-xs text-stone-500">Tap to add a photo</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between border-b border-stone-100 py-1.5"><span className="text-stone-500">Name</span><span>Kōwhai</span></div>
              <div className="flex justify-between border-b border-stone-100 py-1.5"><span className="text-stone-500">Price</span><span>$18</span></div>
              <div className="flex justify-between items-center py-1.5"><span className="text-stone-500">Quantity</span>
                <span className="inline-flex items-center gap-1 bg-stone-100 rounded-full p-0.5"><span className="w-6 h-6 bg-white rounded-full grid place-items-center">−</span><span className="min-w-[1.25rem] text-center">6</span><span className="w-6 h-6 bg-white rounded-full grid place-items-center">+</span></span>
              </div>
            </div>
            <Button className="w-full mt-4 rounded-full bg-[#20251F] hover:bg-black text-white h-10 text-sm">Add to my Gate</Button>
          </div>

          <div className="p-5 sm:p-6 rounded-2xl border border-stone-200 bg-white">
            <p className="font-mono-label text-[11px] uppercase tracking-widest text-[#3F674E]/70">Stock updates itself</p>
            <div className="mt-3 space-y-3">
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
                <p className="text-xs text-stone-500">Jane Smith ordered</p>
                <p className="font-serif-heading text-lg">4 × Kōwhai — $72</p>
              </div>
              <div className="text-center text-stone-400 text-xs">↓</div>
              <div className="rounded-xl border border-[#3F674E]/20 bg-[#3F674E]/10 p-3">
                <p className="text-xs text-[#3F674E]">Kōwhai</p>
                <p className="font-serif-heading text-lg text-[#20251F]"><span className="line-through text-stone-400">6 available</span> <span className="text-[#94AE29] font-medium">→</span> 2 available</p>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-4 leading-relaxed">Orders update your stock automatically. No spreadsheet, no double-selling.</p>
          </div>

          <div className="p-5 sm:p-6 rounded-2xl border border-stone-200 bg-white">
            <p className="font-mono-label text-[11px] uppercase tracking-widest text-[#3F674E]/70">Fulfil it</p>
            <div className="mt-3 rounded-xl border border-stone-200 p-3">
              <p className="font-serif-heading text-base">Jane Smith</p>
              <p className="text-sm text-stone-700 mt-0.5">4 × Kōwhai</p>
              <p className="text-xs text-stone-500 mt-1">Saturday pickup</p>
              <p className="text-sm mt-2"><span className="bg-[#3F674E]/12 text-[#3F674E] px-2 py-0.5 rounded-full text-xs">Paid online ✓</span></p>
              <div className="flex gap-2 mt-3">
                <button className="text-xs px-3 py-1.5 rounded-full bg-[#20251F] text-white">Mark collected</button>
                <button className="text-xs px-3 py-1.5 rounded-full border border-stone-300">Cancel</button>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-4 leading-relaxed">Or accept cash, bank transfer or EFTPOS at pickup — you decide.</p>
          </div>
        </div>
      </section>

      {/* PAYOFF */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 text-left">
        <p className="font-serif-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#20251F] italic leading-[1.05]">"New plants available — shop my Gate."</p>
        <p className="mt-5 text-base sm:text-lg text-stone-700 max-w-2xl leading-relaxed">One link for your customers. One place for your orders and stock.</p>
        <div className="mt-8">
          <Link to="/seller/signup"><Button size="lg" className="bg-[#20251F] hover:bg-black rounded-full h-12 px-6 text-white">Open your Gate</Button></Link>
        </div>
        <p className="mt-6 font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/70">Your Gate. Your customers. Not a marketplace.</p>
      </section>

      <footer className="border-t border-stone-200 py-8">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 flex-wrap">
          <Link to="/" data-testid="footer-brand" className="flex items-center gap-2 group">
            <GMark size={28} className="rounded-md shrink-0" />
            <span className="font-serif-heading text-base tracking-tight">Gate<span className="brand-plus"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <Link to="/faq" data-testid="footer-faq-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">FAQ</Link>
            <Link to="/for-growers" data-testid="footer-growers-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">For growers</Link>
            <Link to="/pricing" data-testid="footer-pricing-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">Pricing</Link>
            <Link to="/policy/seller-policy" data-testid="footer-seller-policy-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">Seller Policy</Link>
            <Link to="/policy/what-can-be-sold" data-testid="footer-what-sold-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">What can be sold</Link>
            <Link to="/policy/privacy" data-testid="footer-privacy-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">Privacy</Link>
            <Link to="/policy/terms" data-testid="footer-terms-link" className="font-mono-label uppercase tracking-widest text-[#3F674E] hover:text-[#20251F]">Terms</Link>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 mt-4" data-testid="footer-copyright-wrap">
          <FooterMeta />
        </div>
      </footer>
    </div>
  );
}
